import React, { useState, useEffect, useCallback } from 'react';
import { getUserKeys, setUserKeys } from '../lib/aiInsights';
import { callGemini } from '../lib/gemini';

/**
 * Collapsible AI settings panel for managing one or more Gemini API keys.
 *
 * Users can add as many keys as they want. The runtime tries them in order
 * (then falls back to the built-in default keys), automatically advancing on
 * 429 rate-limit responses. Keys live only in this browser's localStorage.
 *
 * Props:
 *   isOpen   — boolean, controlled open state
 *   onClose  — callback to close the panel
 */

// Stable per-row id so React keys don't churn when the user types.
let rowIdSeq = 0;
const newRow = (value = '') => ({ id: ++rowIdSeq, value, show: false, status: 'idle', message: '' });

function AiSettings({ isOpen, onClose }) {
  const [rows, setRows] = useState([]);
  const [globalMsg, setGlobalMsg] = useState('');
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const update = () => setIsMobile(window.innerWidth <= 640);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // Load saved keys whenever the panel opens.
  useEffect(() => {
    if (isOpen) {
      const saved = getUserKeys();
      setRows(saved.length ? saved.map((k) => newRow(k)) : [newRow('')]);
      setGlobalMsg('');
    }
  }, [isOpen]);

  const updateRow = useCallback((id, patch) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }, []);

  const handleChange = useCallback((id, value) => {
    updateRow(id, { value, status: 'idle', message: '' });
  }, [updateRow]);

  const handleToggleShow = useCallback((id) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, show: !r.show } : r)));
  }, []);

  const handleAdd = useCallback(() => {
    setRows((prev) => [...prev, newRow('')]);
    setGlobalMsg('');
  }, []);

  const handleRemove = useCallback((id) => {
    setRows((prev) => {
      const next = prev.filter((r) => r.id !== id);
      return next.length ? next : [newRow('')];
    });
    setGlobalMsg('');
  }, []);

  const handleSave = useCallback(() => {
    const cleaned = rows.map((r) => r.value.trim()).filter(Boolean);
    const persisted = setUserKeys(cleaned);
    setRows(persisted.length ? persisted.map((k) => newRow(k)) : [newRow('')]);
    if (persisted.length === 0) {
      setGlobalMsg('All your keys cleared. The built-in default key will be used.');
    } else if (persisted.length === 1) {
      setGlobalMsg('Saved 1 key. The default key remains as a fallback.');
    } else {
      setGlobalMsg(`Saved ${persisted.length} keys. They will be tried in order, then the built-in default.`);
    }
  }, [rows]);

  const handleTest = useCallback(async (id) => {
    const row = rows.find((r) => r.id === id);
    if (!row) return;
    const trimmed = row.value.trim();
    if (!trimmed) {
      updateRow(id, { status: 'error', message: 'Enter a key first.' });
      return;
    }
    updateRow(id, { status: 'testing', message: 'Testing...' });
    try {
      await callGemini(
        trimmed,
        'You are a helpful assistant.',
        'Reply with exactly: "Namaste, connection successful." and nothing else.'
      );
      updateRow(id, { status: 'connected', message: 'Connection successful.' });
    } catch (err) {
      const msg = err.message || 'Test failed.';
      updateRow(id, { status: 'error', message: msg });
    }
  }, [rows, updateRow]);

  if (!isOpen) return null;

  const userKeyCount = rows.filter((r) => r.value.trim()).length;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={{ ...styles.panel, ...(isMobile ? styles.panelMobile : {}) }} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h4 style={styles.title}>AI Settings</h4>
          <button type="button" onClick={onClose} style={styles.closeBtn}>&times;</button>
        </div>

        <p style={styles.summary}>
          {userKeyCount === 0
            ? 'Using the built-in default key only. Add your own for reliable insights.'
            : `${userKeyCount} of your key${userKeyCount > 1 ? 's' : ''} active — the built-in default is used as a final fallback.`}
        </p>

        <label style={styles.label}>Gemini API Keys</label>
        {rows.map((row, idx) => (
          <KeyRow
            key={row.id}
            row={row}
            index={idx}
            isMobile={isMobile}
            onChange={(v) => handleChange(row.id, v)}
            onToggleShow={() => handleToggleShow(row.id)}
            onTest={() => handleTest(row.id)}
            onRemove={() => handleRemove(row.id)}
          />
        ))}

        <button type="button" onClick={handleAdd} style={styles.addBtn}>
          + Add another key
        </button>

        <div style={{ ...styles.actions, ...(isMobile ? styles.actionsMobile : {}) }}>
          <button type="button" onClick={handleSave} style={{ ...styles.saveBtn, ...(isMobile ? styles.actionBtnMobile : {}) }}>
            Save all
          </button>
        </div>

        {globalMsg && (
          <p style={styles.globalMsg}>{globalMsg}</p>
        )}

        <p style={styles.helpText}>
          Keys are tried top-to-bottom. If one hits its rate limit, the next one is used automatically;
          the built-in default key is always tried last.
          <br />
          <a
            href="https://aistudio.google.com/apikey"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.helpLink}
          >
            Get a free API key
          </a>{' '}
          from Google AI Studio. Your keys are stored only in this browser and never sent to any server other than Google.
        </p>
      </div>
    </div>
  );
}

function KeyRow({ row, index, isMobile, onChange, onToggleShow, onTest, onRemove }) {
  const dotColor =
    row.status === 'connected' ? '#2a8a4a'
    : row.status === 'error' ? '#b44'
    : row.status === 'testing' ? 'var(--saffron)'
    : 'var(--text-muted)';

  return (
    <div style={styles.rowWrap}>
      <div style={{ ...styles.inputRow, ...(isMobile ? styles.inputRowMobile : {}) }}>
        <span style={styles.rowIndex}>{index + 1}.</span>
        <input
          type={row.show ? 'text' : 'password'}
          value={row.value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste a Gemini API key"
          style={{ ...styles.input, ...(isMobile ? styles.inputMobile : {}) }}
          autoComplete="off"
          spellCheck={false}
        />
        <button
          type="button"
          onClick={onToggleShow}
          style={{ ...styles.toggleBtn, ...(isMobile ? styles.toggleBtnMobile : {}) }}
          title={row.show ? 'Hide key' : 'Show key'}
        >
          {row.show ? 'Hide' : 'Show'}
        </button>
      </div>
      <div style={styles.rowActions}>
        <span style={{ ...styles.statusDot, background: dotColor }} />
        <span style={styles.statusLabel}>
          {row.status === 'connected' ? 'OK'
            : row.status === 'testing' ? 'Testing...'
            : row.status === 'error' ? 'Error'
            : 'Not tested'}
        </span>
        <button type="button" onClick={onTest} style={styles.smallBtn}>Test</button>
        <button type="button" onClick={onRemove} style={styles.smallBtnDanger}>Remove</button>
      </div>
      {row.message && (
        <p style={{ ...styles.rowMsg, color: row.status === 'error' ? '#b44' : row.status === 'connected' ? '#2a8a4a' : 'var(--text-muted)' }}>
          {row.message}
        </p>
      )}
    </div>
  );
}

// ── Inline Styles ────────────────────────────────────────────────

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 200,
    background: 'rgba(28, 25, 23, 0.25)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
  },
  panel: {
    width: '100%',
    maxWidth: '480px',
    background: 'var(--bg-card)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius)',
    padding: '1.5rem',
    boxShadow: '0 8px 32px rgba(28, 25, 23, 0.12)',
    maxHeight: 'calc(100dvh - 2rem)',
    overflowY: 'auto',
  },
  panelMobile: {
    maxWidth: '100%',
    maxHeight: 'calc(100dvh - 1rem)',
    padding: '1rem',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '0.75rem',
  },
  title: {
    fontFamily: 'var(--font-display)',
    fontSize: '1.05rem',
    fontWeight: 400,
    color: 'var(--text-bright)',
    margin: 0,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '1.4rem',
    color: 'var(--text-muted)',
    cursor: 'pointer',
    lineHeight: 1,
    padding: '0 0.25rem',
  },
  summary: {
    margin: '0 0 1rem',
    fontFamily: 'var(--font-body)',
    fontSize: '0.78rem',
    color: 'var(--text-secondary)',
    lineHeight: 1.5,
  },
  label: {
    display: 'block',
    fontFamily: 'var(--font-body)',
    fontSize: '0.8rem',
    fontWeight: 500,
    color: 'var(--text-secondary)',
    marginBottom: '0.5rem',
  },
  rowWrap: {
    marginBottom: '0.75rem',
    padding: '0.6rem 0.7rem',
    background: 'var(--bg-deep)',
    border: '1px solid var(--border-faint)',
    borderRadius: 'var(--radius)',
  },
  inputRow: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
  },
  inputRowMobile: {
    flexWrap: 'wrap',
  },
  rowIndex: {
    fontFamily: 'var(--font-body)',
    fontSize: '0.78rem',
    color: 'var(--text-muted)',
    flexShrink: 0,
    minWidth: '1.25rem',
  },
  input: {
    flex: 1,
    minWidth: 0,
    padding: '0.45rem 0.6rem',
    fontFamily: 'var(--font-body)',
    fontSize: '0.82rem',
    color: 'var(--text-bright)',
    background: 'var(--bg-card)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius)',
    outline: 'none',
  },
  inputMobile: {
    fontSize: '16px',
  },
  toggleBtn: {
    padding: '0.4rem 0.55rem',
    fontFamily: 'var(--font-body)',
    fontSize: '0.75rem',
    fontWeight: 500,
    color: 'var(--text-muted)',
    background: 'var(--bg-card)',
    border: '1px solid var(--border-faint)',
    borderRadius: 'var(--radius)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  toggleBtnMobile: {
    fontSize: '0.85rem',
  },
  rowActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginTop: '0.45rem',
    flexWrap: 'wrap',
  },
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  statusLabel: {
    fontFamily: 'var(--font-body)',
    fontSize: '0.74rem',
    color: 'var(--text-muted)',
    flex: 1,
    minWidth: 0,
  },
  smallBtn: {
    padding: '0.3rem 0.65rem',
    fontFamily: 'var(--font-body)',
    fontSize: '0.74rem',
    fontWeight: 500,
    color: 'var(--saffron)',
    background: 'var(--saffron-glow)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius)',
    cursor: 'pointer',
  },
  smallBtnDanger: {
    padding: '0.3rem 0.65rem',
    fontFamily: 'var(--font-body)',
    fontSize: '0.74rem',
    fontWeight: 500,
    color: '#a55',
    background: 'transparent',
    border: '1px solid rgba(180, 68, 68, 0.25)',
    borderRadius: 'var(--radius)',
    cursor: 'pointer',
  },
  rowMsg: {
    margin: '0.4rem 0 0',
    fontFamily: 'var(--font-body)',
    fontSize: '0.76rem',
  },
  addBtn: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    fontFamily: 'var(--font-body)',
    fontSize: '0.8rem',
    fontWeight: 500,
    color: 'var(--text-secondary)',
    background: 'transparent',
    border: '1px dashed var(--border-subtle)',
    borderRadius: 'var(--radius)',
    cursor: 'pointer',
    marginBottom: '0.85rem',
  },
  actions: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '0.75rem',
  },
  actionsMobile: {
    flexDirection: 'column',
  },
  actionBtnMobile: {
    width: '100%',
    fontSize: '0.9rem',
    padding: '0.55rem 0.9rem',
  },
  saveBtn: {
    padding: '0.5rem 1rem',
    fontFamily: 'var(--font-body)',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: '#fff',
    background: 'var(--saffron)',
    border: '1px solid var(--saffron)',
    borderRadius: 'var(--radius)',
    cursor: 'pointer',
  },
  globalMsg: {
    margin: '0 0 0.75rem',
    fontFamily: 'var(--font-body)',
    fontSize: '0.8rem',
    color: 'var(--text-secondary)',
  },
  helpText: {
    fontFamily: 'var(--font-body)',
    fontSize: '0.75rem',
    color: 'var(--text-muted)',
    lineHeight: 1.55,
    margin: 0,
  },
  helpLink: {
    color: 'var(--saffron)',
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
  },
};

export default AiSettings;
